import React from 'react';
import { AlertTriangle, Trash2, Ban } from 'lucide-react';
import { Modal, Button } from '../ui';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  loading?: boolean;
  confirmDisabled?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Bevestigen',
  cancelText = 'Annuleren',
  type = 'danger',
  loading = false,
  confirmDisabled = false
}) => {
  const getIcon = () => {
    switch (type) {
      case 'danger':
        return <Trash2 className="h-6 w-6 text-neon-pink-soft" />;
      case 'warning':
        return <Ban className="h-6 w-6 text-warning-500" />;
      default:
        return <AlertTriangle className="h-6 w-6 text-neon-cyan" />;
    }
  };

  const getIconBackground = () => {
    switch (type) {
      case 'danger':
        return 'bg-neon-pink/[.12]';
      case 'warning':
        return 'bg-warning-500/[.12]';
      default:
        return 'bg-neon-cyan/[.12]';
    }
  };

  const getConfirmVariant = (): 'danger' | 'primary' => {
    switch (type) {
      case 'danger':
      case 'warning':
        return 'danger';
      default:
        return 'primary';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="sm:flex sm:items-start">
        <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${getIconBackground()}`}>
          {getIcon()}
        </div>
        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
          <h3 className="text-lg font-bold leading-6 text-fg-bright">
            {title}
          </h3>
          <div className="mt-2">
            <div className="text-sm text-fg-body">
              {typeof message === 'string' ? <p>{message}</p> : message}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse space-y-2 sm:space-y-0 sm:space-x-3 sm:space-x-reverse">
        <Button
          variant={getConfirmVariant()}
          onClick={onConfirm}
          disabled={loading || confirmDisabled}
          className="w-full sm:w-auto"
        >
          {loading ? 'Verwerken...' : confirmText}
        </Button>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={loading}
          className="w-full sm:w-auto"
        >
          {cancelText}
        </Button>
      </div>
    </Modal>
  );
};

export default ConfirmationModal;
